import { useCallback } from 'react';
import { Input } from '@paulownia/shared/components/ui/input';
import { Label } from '@paulownia/shared/components/ui/label';
import { Info, Loader2, Check, X } from 'lucide-react';
import type { SlugStatus } from '../hooks/useSlugValidation';

interface SlugInputProps {
  /** Current slug value */
  value: string;
  /** Current validation status */
  status: SlugStatus;
  /** Called when the user types in the input */
  onChange: (normalizedSlug: string) => void;
  /** Customer app base URL for display */
  baseUrl?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
}

/**
 * Slug input component with real-time validation feedback
 *
 * Features:
 * - Shows the customer app URL prefix
 * - Normalizes input (lowercase, allowed chars only)
 * - Displays validation status (checking, available, taken, invalid)
 * - Shows helpful info about what the slug is used for
 */
export function SlugInput({
  value,
  status,
  onChange,
  baseUrl = 'order-paulownia.web.app/',
  disabled = false,
}: SlugInputProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Normalize slug: lowercase, only allowed characters
      const normalized = e.target.value
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      onChange(normalized);
    },
    [onChange]
  );

  const getStatusDisplay = () => {
    switch (status) {
      case 'checking':
        return {
          icon: <Loader2 className="h-4 w-4 animate-spin" />,
          text: 'Vérification...',
          className: 'text-gray-500',
        };
      case 'available':
        return {
          icon: <Check className="h-4 w-4" />,
          text: 'Disponible',
          className: 'text-green-600',
        };
      case 'taken':
        return {
          icon: <X className="h-4 w-4" />,
          text: 'Cette adresse est déjà utilisée',
          className: 'text-red-600',
        };
      case 'invalid':
        return {
          icon: <X className="h-4 w-4" />,
          text: 'Adresse invalide (min. 3 caractères)',
          className: 'text-red-600',
        };
      default:
        return null;
    }
  };

  const statusDisplay = getStatusDisplay();

  return (
    <div className="space-y-2">
      <Label htmlFor="slug" className="text-sm font-medium">
        Adresse de votre page de commande <span className="text-red-500">*</span>
      </Label>

      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500 whitespace-nowrap">{baseUrl}</span>
        <Input
          id="slug"
          type="text"
          value={value}
          onChange={handleChange}
          placeholder="mon-commerce"
          disabled={disabled}
          className="w-full"
          aria-describedby="slug-status slug-hint"
        />
      </div>

      {/* Validation status */}
      {statusDisplay && (
        <p
          id="slug-status"
          className={`text-sm flex items-center gap-1 ${statusDisplay.className}`}
          role="status"
          aria-live="polite"
        >
          {statusDisplay.icon}
          {statusDisplay.text}
        </p>
      )}

      {/* Helpful info */}
      <div
        id="slug-hint"
        className="bg-blue-50 p-3 rounded-lg flex gap-2 text-sm"
      >
        <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-blue-700">
          C'est l'adresse que vos clients utiliseront pour passer commande. Choisissez quelque
          chose de simple et mémorable.
        </p>
      </div>
    </div>
  );
}
